import {
  type ClientOptions,
  DEFAULT_CONNECT_TIMEOUT_NODE,
  DEFAULT_READ_TIMEOUT_BROWSER,
  DEFAULT_READ_TIMEOUT_NODE,
  DEFAULT_WRITE_TIMEOUT_BROWSER,
  DEFAULT_WRITE_TIMEOUT_NODE,
  type Host,
  type Timeouts,
} from '@algolia/client-common'
import { useMemo } from 'react'

import { useAdServerConfigurationContext } from '../../ad-server'
import {
  FEATURE_TOGGLE_AD_SERVER,
  useFeatureToggle,
} from '../../feature-toggles'
import { processEnvAccessorGetRetailMediaAlgoliaProxyServerUrl } from '../../process-env'
import { isServerSide } from '../../request-context'
import { useAlgoliaConfigContext } from '../context/algoliaConfigContext'

export type AdServerProxyQueryParams = {
  // set flag to '1' for enabling ad server enrichment within algolia proxy
  adServer?: '1'
  // set flag to '1' to exclude ad server rerank campaign type within algolia proxy
  adServerExcludeReRank?: '1'
  // browser conversion id
  asbidc?: string
  // browser targeting id
  asbidt?: string
  categoryId?: string
  expa?: string
  language: string
  pageType: string
  tenant: string
}

const ALGOLIA_CONNECT_TIMEOUT_BROWSER = 2000

/**
 * Algolia request is being sent to proxy service
 * Proxy service gets sponsored hits from retail media ad server
 * Sponsored hits are added into original response
 */
export const useGetProxyClientOptions = (
  additionalParams?: Partial<AdServerProxyQueryParams>,
): ClientOptions => {
  const { algoliaApplicationId, algoliaProxyClientUrl } =
    useAlgoliaConfigContext()
  const { adServerTargeting } = useAdServerConfigurationContext()
  const isAdServerEnabled = useFeatureToggle(FEATURE_TOGGLE_AD_SERVER)

  return useMemo(() => {
    const {
      context: { categoryId, expa, language, pageType, tenant },
      user: { asbidc, asbidt },
    } = adServerTargeting

    const queryParameters: AdServerProxyQueryParams = {
      asbidc,
      asbidt,
      categoryId,
      expa,
      language,
      pageType,
      tenant,
    }

    if (isAdServerEnabled) {
      queryParameters.adServer = '1'
    }

    const serverSide = isServerSide()
    const { hostname, protocol } = new URL(
      serverSide
        ? processEnvAccessorGetRetailMediaAlgoliaProxyServerUrl()
        : algoliaProxyClientUrl,
    )
    /**
     * Temporary solution for bigger response times from proxy
     * It may happen that the handshake takes more than 1s
     * Increase connect timeout from default 1s to 2s
     * Proper solution is in progress
     * See: https://jira.shop-apotheke.com/browse/RMADT-3786
     */
    const timeouts: Timeouts = {
      connect: serverSide
        ? DEFAULT_CONNECT_TIMEOUT_NODE
        : ALGOLIA_CONNECT_TIMEOUT_BROWSER,
      read: serverSide
        ? DEFAULT_READ_TIMEOUT_NODE
        : DEFAULT_READ_TIMEOUT_BROWSER,
      write: serverSide
        ? DEFAULT_WRITE_TIMEOUT_NODE
        : DEFAULT_WRITE_TIMEOUT_BROWSER,
    }

    return {
      baseQueryParameters: {
        ...queryParameters,
        ...additionalParams,
      },
      hosts: [
        {
          accept: 'read',
          /**
           * Remove the trailing colon from the protocol string
           * Assert it as a valid 'protocol' type defined in the Host interface.
           */
          protocol: protocol.slice(0, -1) as Host['protocol'],
          url: hostname,
        },
        // fallback to default Algolia host
        {
          accept: 'read',
          protocol: 'https',
          url: `${algoliaApplicationId}-dsn.algolia.net`,
        },
      ],
      timeouts,
    }
  }, [
    algoliaApplicationId,
    algoliaProxyClientUrl,
    adServerTargeting,
    additionalParams,
    isAdServerEnabled,
  ])
}
